import React, { useState, useEffect, useCallback } from "react";
import BookkeepingService from "../../../../services/BookkeepingService";
import OpeningBalanceEntry from "../BookkeepingTool/OpeningBalanceEntry";
import "./roll-forward-bar.css";

function money(n) {
	if (n === null || n === undefined || n === "") return "";
	let v = typeof n === "number" ? n : parseFloat(n);
	if (isNaN(v)) return "";
	return v.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatementBlock({ title, statement, totalLabel, totalValue }) {
	if (!statement || !Array.isArray(statement.sections)) return null;
	return (
		<div className="rfbar-stmt">
			<div className="rfbar-stmt__title">{title}</div>
			{statement.sections.map((sec, si) => (
				<div key={si} className="rfbar-stmt__section">
					<div className="rfbar-stmt__section-name">{sec.name}</div>
					{(sec.accounts || []).map((a, ai) => (
						<div key={ai} className="rfbar-stmt__row">
							<span className="rfbar-stmt__desc">{a.description}</span>
							<span className="rfbar-stmt__amt">{money(a.current)}</span>
						</div>
					))}
				</div>
			))}
			{totalLabel && (
				<div className="rfbar-stmt__row rfbar-stmt__row--total">
					<span className="rfbar-stmt__desc">{totalLabel}</span>
					<span className="rfbar-stmt__amt">{money(totalValue)}</span>
				</div>
			)}
		</div>
	);
}

export default function RollForwardBar({ clientId, showComparativeColumn, onToggleComparativeColumn, comparativeYearLabel }) {
	const [years, setYears] = useState([]);
	const [loading, setLoading] = useState(false);
	const [showWarn, setShowWarn] = useState(false);
	const [rolling, setRolling] = useState(false);
	const [status, setStatus] = useState(null);
	const [viewYear, setViewYear] = useState(null);
	const [viewData, setViewData] = useState(null);
	const [selectedId, setSelectedId] = useState("");
	const [obOpen, setObOpen] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(false);

	const loadYears = useCallback(async () => {
		if (!clientId) return;
		setLoading(true);
		const res = await BookkeepingService.getEngagementYears({ clientId });
		setYears((res && res.years) || []);
		setLoading(false);
	}, [clientId]);

	useEffect(() => {
		loadYears();
	}, [loadYears]);

	const current = years.find(y => y.isCurrent) || null;

	const canEnterOpenings = !!current && !years.some(y => !y.isCurrent && y.locked);

	const openYear = async y => {
		setViewYear(y);
		setViewData(null);
		const res = await BookkeepingService.getEngagementDeliverable({ wpfEngagementId: y.wpfEngagementId });
		setViewData(res || { error: true });
	};

	const onSelectYear = e => {
		const val = e.target.value;
		setSelectedId(val);
		const y = years.find(x => String(x.wpfEngagementId) === val);
		if (!y) return;
		if (y.isCurrent) {
			window.location.reload();
			return;
		}
		openYear(y);
	};

	const doRollForward = async () => {
		setRolling(true);
		setStatus(null);
		const res = await BookkeepingService.rollForward({ clientId });
		setRolling(false);
		setShowWarn(false);
		if (res && res.newWpfEngagementId) {
			setStatus({
				ok: true,
				msg: `Rolled forward to FYE ${res.fiscal_year_end} — opening ${res.fiscal_year_end}…`,
			});
			setTimeout(() => window.location.reload(), 1400);
		} else {
			setStatus({ ok: false, msg: (res && res.message) || "Roll-forward failed." });
		}
	};

	const deliverable = viewData && !viewData.error ? viewData.deliverable : null;

	return (
		<div className="rfbar">
			<div className="rfbar__group">
				<span className="rfbar__label">Year</span>
				<select className="rfbar__select" value={selectedId || (current ? current.wpfEngagementId : "")} onChange={onSelectYear} disabled={loading || years.length === 0}>
					{years.length === 0 && <option value="">{loading ? "Loading…" : "No files"}</option>}
					{years.map(y => (
						<option key={y.wpfEngagementId} value={y.wpfEngagementId}>
							{y.fiscalYearEnd || "—"}
							{y.isCurrent ? "  (current)" : y.locked ? "  (locked)" : ""}
						</option>
					))}
				</select>
				{years.length > 0 && <span className="rfbar__count">{years.length} year{years.length === 1 ? "" : "s"} on file</span>}
			</div>

			<div className="rfbar__settings-wrap">
				<button className={"rfbar__roll" + (settingsOpen ? " rfbar__roll--active" : "")} onClick={() => setSettingsOpen(!settingsOpen)} title="File settings">
					⚙ Settings
				</button>
				{settingsOpen && (
					<>
						<div className="rfbar__settings-backdrop" onClick={() => setSettingsOpen(false)} />
						<div className="rfbar__settings-menu">
							<div className="rfbar__settings-title">File settings</div>
							{comparativeYearLabel && (
								<div className="rfbar__settings-section">
									<div className="rfbar__settings-heading">Display</div>
									<label className="rfbar__settings-item">
										<input type="checkbox" checked={!!showComparativeColumn} onChange={onToggleComparativeColumn} />
										<span>Show {comparativeYearLabel} comparative column</span>
									</label>
								</div>
							)}
							<div className="rfbar__settings-section">
								<div className="rfbar__settings-heading">Year end</div>
								<button className="rfbar__settings-action" disabled={!current || rolling} onClick={() => { setStatus(null); setSettingsOpen(false); setShowWarn(true); }}>
									Roll forward to next year
								</button>
								{canEnterOpenings && (
									<button className="rfbar__settings-action" onClick={() => { setStatus(null); setSettingsOpen(false); setObOpen(true); }}>
										Opening balances
									</button>
								)}
							</div>
						</div>
					</>
				)}
			</div>

			{status && <span className={"rfbar__status " + (status.ok ? "rfbar__status--ok" : "rfbar__status--err")}>{status.msg}</span>}

			{}
			{showWarn && (
				<div className="rfbar__overlay" onClick={() => !rolling && setShowWarn(false)}>
					<div className="rfbar__modal" onClick={e => e.stopPropagation()}>
						<div className="rfbar__modal-title">⚠ Roll forward — heads up</div>
						<p className="rfbar__modal-text">This feature is new and still being validated. Rolling forward will:</p>
						<ul className="rfbar__modal-list">
							<li>create <strong>next year's compilation file</strong> (attaching to next year's engagement, or creating one if none exists yet);</li>
							<li>write to the <strong>live production</strong> database;</li>
							<li>carry forward settings, notes and schedules — openings &amp; comparatives derive from this year's signed statements.</li>
						</ul>
						<p className="rfbar__modal-text">This is a warning, not a block — you can proceed.</p>
						<div className="rfbar__modal-actions">
							<button className="rfbar__btn rfbar__btn--ghost" disabled={rolling} onClick={() => setShowWarn(false)}>Cancel</button>
							<button className="rfbar__btn rfbar__btn--warn" disabled={rolling} onClick={doRollForward}>{rolling ? "Rolling forward…" : "Roll forward anyway"}</button>
						</div>
					</div>
				</div>
			)}

			{}
			{viewYear && (
				<div className="rfbar__overlay" onClick={() => { setViewYear(null); setSelectedId(current ? String(current.wpfEngagementId) : ""); }}>
					<div className="rfbar__modal rfbar__modal--wide" onClick={e => e.stopPropagation()}>
						<div className="rfbar__modal-title">
							Working papers — FYE {viewYear.fiscalYearEnd}
							{viewYear.locked ? " (locked)" : ""}
						</div>
						{!viewData && <p className="rfbar__modal-text">Loading…</p>}
						{viewData && viewData.error && <p className="rfbar__modal-text">Could not load this year.</p>}
						{viewData && !viewData.error && !deliverable && (
							<p className="rfbar__modal-text">This year has no signed statements yet — nothing locked to view. (Only finalized years show frozen statements here.)</p>
						)}
						{deliverable && (
							<div className="rfbar-stmts">
								<StatementBlock title="Balance Sheet" statement={deliverable.balance_sheet} totalLabel="Total Assets" totalValue={deliverable.total_assets} />
								<StatementBlock title="Income Statement" statement={deliverable.income_statement} totalLabel="Net Income" totalValue={deliverable.net_income} />
							</div>
						)}
						<div className="rfbar__modal-actions">
							<button className="rfbar__btn rfbar__btn--ghost" onClick={() => { setViewYear(null); setSelectedId(current ? String(current.wpfEngagementId) : ""); }}>Close</button>
						</div>
					</div>
				</div>
			)}

			{}
			{obOpen && (
				<div className="rfbar__overlay" onClick={() => setObOpen(false)}>
					<div className="rfbar__modal rfbar__modal--wide" onClick={e => e.stopPropagation()} style={{ maxHeight: "85vh", overflowY: "auto" }}>
						<div className="rfbar__modal-title">Opening balances — FYE {current ? current.fiscalYearEnd : ""}</div>
						<OpeningBalanceEntry
							clientId={clientId}
							wpfEngagementId={current ? current.wpfEngagementId : null}
							source="manual"
							onClose={() => setObOpen(false)}
							onComplete={res => {
								setObOpen(false);
								setStatus({ ok: true, msg: `Saved ${res.saved} prior-year account${res.saved === 1 ? "" : "s"}.` });
							}}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
